// js for shopable video
// Guard the component registration so multiple shoppable-video sections can
// safely load this asset on the same page.
if (!customElements.get("shopable-video")) {
class ShopableVideo extends PopupBase {
  constructor() {
    super();
    this.popupDiv = this.querySelector(".video-item__popup");
    this.init();
  }
  init() {
    if (this.popupDiv || !this.classList.contains("instagram-item")) {
      window.addEventListener("resize", this.responsive.bind(this));
      window.addEventListener("load", this.responsive.bind(this));
      this.addEventListener("click", this.onClick.bind(this));
      this.connectedCallback();
    }
  }
  onClick() {
    // Opening the modal is a direct user gesture, so the selected video is
    // allowed to start with sound. Card previews remain muted for autoplay.
    this.soundRequested = true;
    const template = this.closest(".shopable-video-section").querySelector(
      "template"
    );
    if (template) {
      const content =
        template.content.firstElementChild.cloneNode(true).outerHTML;
      const nav = `<div class="swiper-arrow swiper-prev-modal transition swiper-button-prev bg-white btn-hover inline-flex content-center border rounded-50 overflow-hidden">
            <svg width="6" height="11" fill="none">
              <use href="#icon-back"></use>
            </svg>
          </div>
          <div class="swiper-arrow swiper-next-modal transition swiper-button-next bg-white btn-hover inline-flex content-center border rounded-50 overflow-hidden">
            <svg width="6" height="11" fill="none">
              <use href="#icon-next"></use>
            </svg>
          </div>`;
      this.initPopup(content, nav);
      this.findActiveSlideModal();
      const modal = this.modal;
      if (!modal) return;
      const productPopupDiv = modal.modalBoxContent.querySelectorAll(
        ".video-item__product-mobile"
      );
      if (productPopupDiv.length > 0){
        productPopupDiv.forEach((div) => {
          div.addEventListener(
            "click",
            this.onClickMobile.bind(this)
          );
        });
      }
    }
  }

  findActiveSlideModal() {
    const modal = this.modal;
    if (!modal) return;
    const swiperContainer = modal.modalBoxContent.querySelector(
      "slide-section-popup"
    );
    if (!swiperContainer) return;
    const position = this.dataset.position;
    if (!position) return;
    if (position !== null && !isNaN(parseInt(position))) {
      const slideIndex = parseInt(position);
      swiperContainer.swiper.slideTo(slideIndex, 0, false);
    } else {
      swiperContainer.swiper.slideTo(0, 0, false);
    }
    this.bindModalVideo(swiperContainer.swiper);
    this.bindStoryControls();
    this.playActiveModalVideo(swiperContainer.swiper);
  }

  bindModalVideo(swiper) {
    if (!swiper || this.modalVideoSwiper === swiper) return;
    this.modalVideoSwiper = swiper;
    swiper.on("slideChangeTransitionStart", () => {
      this.playActiveModalVideo(swiper);
    });
  }

  playActiveModalVideo(swiper) {
    if (!swiper) return;
    const modalRoot =
      this.modal?.modal ||
      document.querySelector(".tingle-modal.shopable-video");
    if (!modalRoot) return;

    modalRoot.querySelectorAll("video").forEach((video) => {
      video.pause();
      video.muted = true;
    });

    const activeSlide =
      swiper.slides?.[swiper.activeIndex] ||
      modalRoot.querySelector("slide-section-popup .swiper-slide-active");
    const activeVideo = activeSlide?.querySelector("video");
    if (!activeVideo) return;

    // The popup contains every story so customers can swipe between them,
    // but only the selected video receives a source and starts downloading.
    const deferredSource = activeVideo.dataset.src;
    if (deferredSource && !activeVideo.getAttribute("src")) {
      activeVideo.src = deferredSource;
      activeVideo.removeAttribute("data-src");
      activeVideo.load();
    }

    if (window.innerWidth < 768) {
      activeVideo.controls = false;
    }
    activeVideo.removeAttribute("muted");
    activeVideo.defaultMuted = false;
    activeVideo.muted = false;
    activeVideo.volume = 1;
    this.updateStoryControls(activeSlide, activeVideo);
    const playPromise = activeVideo.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        // Keep the video unmuted so one tap on the native play control starts
        // it with sound if the browser requires one more explicit gesture.
        activeVideo.muted = false;
        this.updateStoryControls(activeSlide, activeVideo);
      });
    }
  }

  bindStoryControls() {
    const modalRoot =
      this.modal?.modal ||
      document.querySelector(".tingle-modal.shopable-video");
    if (!modalRoot) return;

    modalRoot.querySelectorAll("[data-zinney-video-play]").forEach((button) => {
      if (button.dataset.zinneyBound === "true") return;
      button.dataset.zinneyBound = "true";
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const slide = button.closest(".swiper-slide");
        const video = slide?.querySelector("video");
        if (!video) return;
        if (video.paused) {
          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => {});
        } else {
          video.pause();
        }
        this.updateStoryControls(slide, video);
      });
    });

    modalRoot.querySelectorAll("[data-zinney-video-mute]").forEach((button) => {
      if (button.dataset.zinneyBound === "true") return;
      button.dataset.zinneyBound = "true";
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const slide = button.closest(".swiper-slide");
        const video = slide?.querySelector("video");
        if (!video) return;
        video.muted = !video.muted;
        if (!video.muted) video.volume = 1;
        this.updateStoryControls(slide, video);
      });
    });

    modalRoot.querySelectorAll(".swiper-slide video").forEach((video) => {
      if (video.dataset.zinneyStoryBound === "true") return;
      video.dataset.zinneyStoryBound = "true";
      if (window.innerWidth < 768) video.controls = false;
      ["play", "pause", "volumechange"].forEach((eventName) => {
        video.addEventListener(eventName, () => {
          const slide = video.closest(".swiper-slide");
          this.updateStoryControls(slide, video);
        });
      });
      this.updateStoryControls(video.closest(".swiper-slide"), video);
    });
  }

  updateStoryControls(slide, video) {
    if (!slide || !video) return;
    const playButton = slide.querySelector("[data-zinney-video-play]");
    const muteButton = slide.querySelector("[data-zinney-video-mute]");
    if (playButton) {
      playButton.classList.toggle("is-paused", video.paused);
      playButton.setAttribute(
        "aria-label",
        video.paused ? "Play video" : "Pause video"
      );
    }
    if (muteButton) {
      muteButton.classList.toggle("is-muted", video.muted);
      muteButton.setAttribute(
        "aria-label",
        video.muted ? "Unmute video" : "Mute video"
      );
    }
  }

  onCloseEvent() {
    const popup =
      this.modal?.modal ||
      document.querySelector(".tingle-modal.shopable-video");
    if (!popup) return;
    popup.querySelectorAll("video").forEach((video) => {
      video.pause();
      video.muted = true;
    });
    this.soundRequested = false;
    this.modalVideoSwiper = null;
    const productInformation = popup.querySelector(
      ".shopable-video__product-information"
    );
    if (!productInformation) return;
    productInformation.classList.add("hidden");
  }
  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      const videos = this.querySelectorAll("video");
      videos.forEach((video) => {
        const dataSrc = video.dataset.src;
        if (dataSrc) {
          video.src = dataSrc;
          video.removeAttribute("data-src");
          video.preload = "none";
        }
      });
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 200px 0px",
    }).observe(this);
  }
  responsive() {
    const productDivs = document.querySelectorAll(
      ".shopable-video__product-information"
    );
    if (productDivs.length != 0) {
      if (window.innerWidth >= 768) {
        const flexDiv = this.popupDiv?.querySelector(
          ".video-item__popup--flex"
        );
        if (!flexDiv) return;
        productDivs.forEach((div) => {
          div.classList.remove("hidden");
        });
      } else {
        const flexDiv = this.popupDiv?.querySelector(
          ".video-item__popup--flex"
        );
        if (!flexDiv) return;
        productDivs.forEach((div) => {
          div.classList.add("hidden");
        });
      }
    }
  }
  onClickMobile(event) {
    const _this = this;
    const currentTarget = event.currentTarget;
    const popup = currentTarget.closest(".video-item__popup");
    if (!popup) return;
    const productInformation = popup.querySelector(
      ".shopable-video__product-information"
    );
    if (!productInformation) return;
    const slidePopup =
      popup.closest("slide-section-popup") ||
      this.modal?.modalBoxContent?.querySelector("slide-section-popup");
    if (slidePopup?.swiper) {
      slidePopup.swiper.allowTouchMove = false;
      slidePopup.swiper.unsetGrabCursor();
    }
    productInformation.classList.remove("hidden");
    popup.classList.add("open");
    if (_this.modal?.modalCloseBtn) {
      _this.modal.modalCloseBtn.style.display = "none";
    }
  }
}
customElements.define("shopable-video", ShopableVideo);

class SlideSectionPopup extends SlideSection {
  constructor() {
    super();
  }
}
customElements.define("slide-section-popup", SlideSectionPopup);

class ShopableOverlay extends ShopableVideo {
  constructor() {
    super();
    this.addEventListener("click", this.onClick.bind(this));
  }

  onClick(event) {
    const currentTarget = event.currentTarget;
    const modal = currentTarget.closest(".shopable-video");
    const popup = currentTarget?.closest(".video-item__popup");
    if (!popup) return;
    const productInformation = popup.querySelector(
      ".shopable-video__product-information"
    );
    const modalCloseBtn = modal?.querySelector(".tingle-modal__close");
    if (!productInformation) return;
    const slidePopup =
      popup.closest("slide-section-popup") ||
      modal?.querySelector("slide-section-popup");
    if (slidePopup?.swiper) {
      slidePopup.swiper.allowTouchMove = true;
      slidePopup.swiper.setGrabCursor();
    }
    productInformation.classList.add("hidden");
    popup.classList.remove("open");
    modalCloseBtn?.removeAttribute("style");
  }
}
customElements.define("shopable-overlay", ShopableOverlay);

class ButtonCloseShopable extends ShopableOverlay {
  constructor() {
    super();
    this.addEventListener("click", this.onClick.bind(this));
  }
}
customElements.define("button-close-shopable", ButtonCloseShopable);
}
